function parseCalendarDate(value) {
  if (typeof value !== "string" || !value.trim()) return null;

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return { year, month, day };
  }

  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return null;
  return {
    year: timestamp.getFullYear(),
    month: timestamp.getMonth() + 1,
    day: timestamp.getDate(),
  };
}

function calendarOrdinal({ year, month, day }) {
  return Date.UTC(year, month - 1, day);
}

function formatCalendarDate(date) {
  return new Date(date.year, date.month - 1, date.day).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function withLatestUpdateStatus(label, latestUpdateStatus) {
  const status = String(latestUpdateStatus || "").trim().toLowerCase();
  if (["running", "started", "processing"].includes(status)) return `${label} · Analysis in progress`;
  if (["failed", "error"].includes(status)) return `${label} · Latest update failed`;
  if (["invalid", "incomplete"].includes(status)) return `${label} · Latest update incomplete`;
  return label;
}

export function getLocalRunCompletionLabel(run, now = new Date(), latestUpdateStatus = null) {
  if (!run) return withLatestUpdateStatus("No completed analysis available yet", latestUpdateStatus);

  const completed = parseCalendarDate(run.completed_at);
  if (!completed) {
    return withLatestUpdateStatus("Analysis completion date unavailable", latestUpdateStatus);
  }

  const label = `Analysis completed: ${formatCalendarDate(completed)}`;
  const current = { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  const daysOld = Math.floor((calendarOrdinal(current) - calendarOrdinal(completed)) / 86400000);
  const withFreshness = daysOld > 35 ? `${label} · Older analysis` : label;
  return withLatestUpdateStatus(withFreshness, latestUpdateStatus);
}

export function getLocalRunFreshnessLabel(runDate, now = new Date(), latestUpdateStatus = null) {
  const analyzed = parseCalendarDate(runDate);
  const status = String(latestUpdateStatus || "").trim().toLowerCase();
  if (!analyzed) {
    const lastRun = runDate ? "Last analyzed date unavailable" : "No completed analysis available yet";
    if (["running", "started", "processing"].includes(status)) return `Analysis in progress · ${lastRun}`;
    if (["failed", "error"].includes(status)) return `Latest update failed · ${lastRun}`;
    if (["invalid", "incomplete"].includes(status)) return `Latest update incomplete · ${lastRun}`;
    return lastRun;
  }

  const formattedDate = formatCalendarDate(analyzed);
  const current = { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  const daysOld = Math.floor((calendarOrdinal(current) - calendarOrdinal(analyzed)) / 86400000);

  const lastAnalyzed = `Last analyzed: ${formattedDate}`;
  if (["running", "started", "processing"].includes(status)) return `${lastAnalyzed} · Analysis in progress`;
  if (["failed", "error"].includes(status)) return `${lastAnalyzed} · Latest update failed`;
  if (["invalid", "incomplete"].includes(status)) return `${lastAnalyzed} · Latest update incomplete`;
  return daysOld > 35 ? `${lastAnalyzed} · Older analysis` : lastAnalyzed;
}
